import React from 'react';
import { ANALYTICS_ENTRY_POINT_STATE_KEY } from '@/utils/analytics/reportEntryPoint';

import { NavLink, useHistory, useLocation } from 'react-router-dom';

import { useMakeFilteredUrl } from '@/hooks/useFilter';
import { useHashStateBool } from '@/hooks/useQueryState';
import { cn } from '@/utils/utils';
import {
  ReportType,
  useChecklistModuleEnabled,
  useAvailableReportTypes,
} from '@/modules/reports/ReportTypes';
import FullScreenQRScanner from '@/modules/maquinista/FullScreenQRScanner';
import { useCan } from '@/hooks/useRbac';
import { useInProgressWorkOrders } from '@/hooks/useInProgressWorkOrders';
import { isChecklistFillRoute } from '@/modules/checklists/fill/checklistFillRoute';
import { MachinistWOTour } from '@/components/GuidedTourProvider';
import { Badge } from '@/components/ui/badge';
import {
  BellIcon,
  CameraIcon,
  FileTextIcon,
  FuelIcon,
  GaugeIcon,
  HomeIcon,
  ListChecksIcon,
  PlusCircleIcon,
  WrenchIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { atom, useAtom, useSetAtom } from 'jotai';

interface NavItemProps {
  Icon: React.ElementType;
  badge?: number;
  to: string;
  name: string;
  exact?: boolean;
  /** Overrides NavLink's prefix match on `to` (react-router v5 `isActive`). */
  isActive?: (match: unknown, location: { pathname: string }) => boolean;
}

const NavItem = ({ Icon, badge, to, name, exact, isActive }: NavItemProps) => (
  <NavLink
    to={to}
    className={cn(
      'inline-flex flex-col items-center justify-center px-5 group',
      'text-sidebar-accent-foreground! hover:text-sidebar-accent-foreground'
    )}
    activeClassName="bg-sidebar-accent-foreground/15 p-1 rounded-lg m-1.5 px-3.5"
    exact={exact}
    isActive={isActive}
  >
    <div className="relative">
      <Icon className="size-6 mb-1" />
      {!!badge && (
        <Badge className="size-4 p-0 absolute top-0 right-0 -translate-y-1/2 translate-x-full flex items-center justify-center bg-red-500 text-white">
          {badge}
        </Badge>
      )}
    </div>
    <span className="text-xs">{name}</span>
  </NavLink>
);

/** The user's first permitted report type (a mechanic may have no daily
 * reports); the bare list only when there is none. */
export const partesTabPath = (
  available: ReadonlyArray<{ value: ReportType }>
): string =>
  available.length > 0 ? `/my-reports/${available[0].value}` : '/my-reports';

const MachinistFooter: React.FC = () => {
  const toggleNewReportSheet = useToggleNewReportSheet();
  const make = useMakeFilteredUrl();
  const availableReportTypes = useAvailableReportTypes();
  const [isOpenAddDaily, setIsOpenAddDaily] = useHashStateBool(
    'modal-add-daily',
    'push'
  );
  const location = useLocation();
  const { state } = location;
  const { count: numInProgressWO } = useInProgressWorkOrders();

  // The checklist fill flow renders as a focused full-screen flow — hide the
  // bottom nav (and its "Crear" FAB, which otherwise overlaps the fill flow's
  // own Anterior/Siguiente/Enviar bar) while it's open.
  if (isChecklistFillRoute(location.pathname)) return null;

  return (
    <Sheet>
      <NewReportSheet />
      <MachinistWOTour />
      <FullScreenQRScanner
        key={'' + isOpenAddDaily}
        isOpen={isOpenAddDaily}
        toggle={() => window.history.back()}
        reportType={(state as any)?.reportType || undefined}
      />
      <div className={cn('z-40 w-full h-16 bg-background border-t')}>
        <div className={cn('grid h-full grid-cols-5')}>
          <NavItem Icon={HomeIcon} to="" name="Inicio" exact={true} />
          <NavItem
            Icon={FileTextIcon}
            to={make(partesTabPath(availableReportTypes))}
            name="Partes"
            // lit on every report list and detail, whatever the type
            isActive={(_, location) =>
              location.pathname.includes('/my-reports')
            }
          />
          <Button onClick={toggleNewReportSheet} asChild>
            <div
              className={cn(
                'inline-flex flex-col items-center justify-center px-5 group h-auto shadow-none',
                'relative mt-0',
                'text-sidebar-accent-foreground hover:text-sidebar-accent-foreground/70 bg-transparent hover:bg-transparent'
              )}
            >
              <div className="absolute -top-5 flex size-14 items-center justify-center rounded-full bg-success text-white shadow-lg">
                <PlusCircleIcon className="size-7" />
              </div>
              <span className={cn('text-xs mt-7')}>Crear</span>
            </div>
          </Button>
          <NavItem
            Icon={WrenchIcon}
            to={make(
              `/my-work-orders${numInProgressWO ? '?status=IN_PROGRESS' : ''}`
            )}
            name="OTs"
            badge={numInProgressWO}
          />
          <Button
            variant="ghost"
            className={cn(
              'w-full h-full py-0 gap-0 justify-start text-lg',
              'inline-flex flex-col items-center justify-center px-5 group',
              'text-sidebar-accent-foreground hover:bg-transparent'
            )}
            onClick={() => {
              setIsOpenAddDaily(true);
            }}
          >
            <CameraIcon className="size-6 mb-1" />
            <span className="text-xs">QR</span>
          </Button>
        </div>
      </div>
    </Sheet>
  );
};

export default MachinistFooter;

const newReportOpenAtom = atom(false);
export const useToggleNewReportSheet = () => {
  const setOpen = useSetAtom(newReportOpenAtom);
  return () => {
    setOpen((prev) => !prev);
  };
};

const FOOTER_ENTRY = { [ANALYTICS_ENTRY_POINT_STATE_KEY]: 'footer_sheet' };

const NewReportSheet = () => {
  const [isOpen, setOpen] = useAtom(newReportOpenAtom);
  const can = useCan();
  const checklistModuleEnabled = useChecklistModuleEnabled();
  const history = useHistory();
  const make = useMakeFilteredUrl();
  return (
    <Sheet open={isOpen} onOpenChange={setOpen}>
      <SheetContent
        side="bottom"
        className="max-h-[60%] overflow-y-auto rounded-t-3xl"
      >
        <SheetHeader>
          <SheetTitle>Crear Nuevo</SheetTitle>
        </SheetHeader>
        <div className="grid grid-cols-2 gap-4 py-6">
          <CreateOption
            icon={<FileTextIcon className="size-6 text-blue-600" />}
            title="Parte Diario"
            description="Registrar horas de trabajo"
            disabled={!can(`create-report:${ReportType.DailyReport}`)}
            onClick={() => {
              history.push(
                `/my-reports/${ReportType.DailyReport}/new`,
                FOOTER_ENTRY
              );
            }}
          />
          {/* <CreateOption
            icon={<Wrench className="size-6 text-orange-600" />}
            title="Orden de Trabajo"
            description="Crear nueva OT"
            onClick={() => {
              // setOpenCreateSheet(false)
              // router.push("/crear/orden-trabajo")
            }}
          /> */}
          <CreateOption
            icon={<BellIcon className="size-6 text-amber-600" />}
            title="Novedad"
            description="Reportar alertas u observaciones"
            disabled={!can(`create-report:${ReportType.MachineNotice}`)}
            onClick={() =>
              history.push(
                make(`/my-reports/${ReportType.MachineNotice}/new`),
                FOOTER_ENTRY
              )
            }
          />
          <CreateOption
            icon={<FuelIcon className="size-6 text-green-600" />}
            title="Despacho"
            description="Registrar despacho de combustible"
            disabled={!can(`create-report:${ReportType.FuelReport}`)}
            onClick={() =>
              history.push(
                make(`/my-reports/${ReportType.FuelReport}/new`),
                FOOTER_ENTRY
              )
            }
          />
          <CreateOption
            icon={<GaugeIcon className="size-6 text-purple-600" />}
            title="Stock"
            description="Registrar stock de combustible"
            disabled={!can(`create-report:${ReportType.StockReport}`)}
            onClick={() =>
              history.push(
                make(`/my-reports/${ReportType.StockReport}/new`),
                FOOTER_ENTRY
              )
            }
          />
          <CreateOption
            icon={<ListChecksIcon className="size-6 text-cyan-600" />}
            title="Checklist"
            description="Inspeccionar un equipo"
            disabled={
              !can(`create-report:${ReportType.Checklist}`) ||
              !checklistModuleEnabled
            }
            onClick={() =>
              history.push(make(`/my-reports/${ReportType.Checklist}/new`))
            }
          />
        </div>
      </SheetContent>
    </Sheet>
  );
};

interface CreateOptionProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
  disabled?: boolean;
}

const CreateOption = ({
  icon,
  title,
  description,
  onClick,
  disabled,
}: CreateOptionProps) => {
  return (
    <SheetTrigger asChild>
      <Button
        className="flex flex-col items-center gap-2 rounded-xl border p-4 text-center hover:bg-sidebar-accent h-auto"
        variant="ghost"
        onClick={onClick}
        disabled={disabled}
      >
        <div className="rounded-full bg-sidebar-accent p-3">{icon}</div>
        <div className="relative w-full justify-items-center">
          <h3 className="font-medium">{title}</h3>
          <span className="text-xs text-sidebar-foreground max-w-4/5 whitespace-break-spaces">
            {description}
          </span>
        </div>
      </Button>
    </SheetTrigger>
  );
};
